import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from './lib/react-query'
import { AuthProvider } from './contexts/AuthContext'
import { ProtectedLayout } from './components/layout/ProtectedLayout'
import { LoginPage } from './pages/Login'
import { AuthCallback } from './pages/AuthCallback'
import { Dashboard } from './pages/Dashboard'
import { NetworkTopologyPage } from './pages/NetworkTopology'
import { CRDTOperationsPage } from './pages/CRDTOperations'
import { SecurityPage } from './pages/Security'
import { AIMCPPage } from './pages/AIMCP'
import { ConsensusPage } from './pages/Consensus'
import { NodesPage } from './pages/Nodes'
import { PlatformDashboard } from './pages/admin/PlatformDashboard'
import { PlatformAdminRoute } from './components/auth/ProtectedRoute'

// Tenant pages
import TenantUsers from './pages/tenant/Users'
import UserDetail from './pages/tenant/UserDetail'
import TenantRoles from './pages/tenant/Roles'

import { StoragePage } from './pages/Storage'

import { AlertsPage } from './pages/Alerts'
import { OrchestrationPage } from './pages/Orchestration'
import { ConfigurationPage } from './pages/Configuration'

const NotFoundPage = () => (
  <div className="space-y-6">
    <div className="text-center py-16">
      <h1 className="text-6xl suse-bold text-console-muted mb-4">404</h1>
      <h2 className="text-2xl suse-medium text-console-text mb-4">Page Not Found</h2>
      <p className="text-console-muted mb-8">The page you&apos;re looking for doesn&apos;t exist.</p>
      <button 
        onClick={() => window.location.href = '/'}
        className="btn-primary"
      >
        Return to Dashboard
      </button>
    </div>
  </div>
);

// Create router with future flags enabled
const router = createBrowserRouter(
  [
    {
      path: '/login',
      element: <LoginPage />,
    },
    {
      path: '/auth/callback',
      element: <AuthCallback />,
    },
    {
      path: '/',
      element: <ProtectedLayout />,
      children: [
        { index: true, element: <Dashboard /> },
        { path: 'consensus', element: <ConsensusPage /> },
        { path: 'nodes', element: <NodesPage /> },
        { path: 'network', element: <NetworkTopologyPage /> },
        { path: 'crdt', element: <CRDTOperationsPage /> },
        { path: 'security', element: <SecurityPage /> },
        { path: 'storage', element: <StoragePage /> },
        { path: 'alerts', element: <AlertsPage /> },
        { path: 'orchestration', element: <OrchestrationPage /> },
        { path: 'config', element: <ConfigurationPage /> },
        { path: 'ai-mcp', element: <AIMCPPage /> },
        // Platform Admin Routes
        { path: 'admin/platform', element: <PlatformAdminRoute><PlatformDashboard /></PlatformAdminRoute> },
        // Tenant Management Routes
        { path: 'tenant/users', element: <TenantUsers /> },
        { path: 'tenant/users/:id', element: <UserDetail /> },
        { path: 'tenant/roles', element: <TenantRoles /> },
        { path: '*', element: <NotFoundPage /> },
      ],
    },
  ],
  {
    future: {
      v7_relativeSplatPath: true,
    },
  },
);

function App() {
  console.log('[APP] Rendering App component');
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <div className="App">
          <RouterProvider router={router} />
        </div>
        
        {/* React Query DevTools */}
        <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;